import { initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  updateDoc
} from "firebase/firestore";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInAnonymously,
  setPersistence,
  browserLocalPersistence,
  RecaptchaVerifier
} from "firebase/auth";
import Cookies from "js-cookie";
import { Randomcode } from "./components_root/rendom_code";
import { getStorage } from "firebase/storage";

/* ============================
   🔹 Firebase Config
============================ */
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

/* ============================
   🔹 Init Firebase
============================ */
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const provider = new GoogleAuthProvider();
export const StorageMain = getStorage(app);
const db = getFirestore(app);
export const dbstatus = db;

/* ============================
   🔐 Auth Persistence
============================ */
setPersistence(auth, browserLocalPersistence).catch(console.error);

/* ============================
   🔐 Anonymous Auth (Safe)
============================ */
let authPromise = null;

const ensureFirebaseAuth = async () => {
  if (auth.currentUser) return;

  if (!authPromise) {
    authPromise = signInAnonymously(auth)
      .catch(err => {
        authPromise = null;
        throw err;
      });
  }

  await authPromise;
};

/* ============================
   🔐 Recaptcha
============================ */
export const setupRecaptcha = (containerId = "recaptcha-container") => {
  if (typeof window === "undefined") return null;

  if (window.recaptchaVerifier) return window.recaptchaVerifier;

  try {
    const verifier = new RecaptchaVerifier(
      containerId,
      { size: "invisible" },
      auth
    );

    window.recaptchaVerifier = verifier;
    return verifier;
  } catch (error) {
    console.error("Recaptcha error:", error);
    return null;
  }
};

/* ============================
   🍪 Cookie Helper
============================ */
const getCookieOptions = () => {
  const isHttps = window.location.protocol === "https:";
  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  return {
    expires: 90,
    path: "/",
    secure: isHttps,
    sameSite: isHttps ? "None" : "Lax",
    ...(isHttps && !isLocalhost && { domain: ".aooz.in" }),
  };
};

/* ============================
   🔐 AUTO LOGIN
============================ */
export const auto_signInWithaooz_user = async () => {
  try {
    const emailCookie = Cookies.get("emailid");

    if (!emailCookie) {
      return { success: false };
    }

    const emailid = emailCookie.toLowerCase().trim();
    const account_guid = btoa(emailid);

    let displayName = Cookies.get("username") || "User";
    let photoURL = Cookies.get("photoURL") || "";
    let currency_code = "INR";

    const checkQuery = query(
      collection(db, "accounts"),
      where("email", "==", emailid)
    );

    const snap = await getDocs(checkQuery);

    if (!snap.empty) {
      const userData = snap.docs[0].data();

      displayName = userData.name || displayName;
      photoURL = userData.photoURL || photoURL;

      await updateDoc(doc(db, "accounts", account_guid), {
        last_login: new Date(),
        updated_date_time: new Date(),
      });

    } else {
      await setDoc(doc(db, "accounts", account_guid), {
        guid: account_guid,
        email: emailid,
        name: displayName,
        photoURL,
        currency_code,
        username:
          displayName.replace(/\s+/g, "").toLowerCase() +
          Math.floor(Math.random() * 1000),
        status: true,
        verification: false,
        date_time: new Date(),
        updated_date_time: new Date(),
        last_login: new Date(),
      });
    }

    const sessionToken = Randomcode(12);

    await setDoc(doc(db, "accounts_login", sessionToken), {
      guid: account_guid,
      sessiontoken: sessionToken,
      email: emailid,
      name: displayName,
      photoURL,
      device: navigator.userAgent,
      login_time: new Date(),
      status: true,
    });

    const cookieOptions = getCookieOptions();

    // Clear old
    Cookies.remove("sessiontoken", { path: "/" });
    Cookies.remove("guid", { path: "/" });

    // Set new
    Cookies.set("status_user", "login", cookieOptions);
    Cookies.set("emailid", emailid, cookieOptions);
    Cookies.set("username", displayName, cookieOptions);
    Cookies.set("guid", account_guid, cookieOptions);
    Cookies.set("photoURL", photoURL, cookieOptions);
    Cookies.set("sessiontoken", sessionToken, cookieOptions);

    return { success: true };

  } catch (error) {
    console.error("Auto login error:", error);
    return { success: false };
  }
};

/* ============================
   🔐 GOOGLE LOGIN
============================ */
export const signInWithGoogle = async () => {
  try {
    // Clear old session
    Cookies.remove("emailid");
    Cookies.remove("guid");
    Cookies.remove("sessiontoken");

    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    if (!user?.email) throw new Error("No email");

    const emailid = user.email.toLowerCase().trim();
    let account_guid = btoa(emailid);

    let displayName = user.displayName || "User";
    let photoURL = user.photoURL || "";

    const checkQuery = query(
      collection(db, "accounts"),
      where("email", "==", emailid)
    );

    const snap = await getDocs(checkQuery);

    if (!snap.empty) {
      const data = snap.docs[0].data();

      account_guid = data.guid;
      displayName = data.name || displayName;
      photoURL = data.photoURL || photoURL;

      await updateDoc(doc(db, "accounts", account_guid), {
        last_login: new Date(),
      });

    } else {
      await setDoc(doc(db, "accounts", account_guid), {
        guid: account_guid,
        email: emailid,
        name: displayName,
        photoURL,
        status: true,
        date_time: new Date(),
        last_login: new Date(),
      });
    }

    const sessionToken = Randomcode(12);

    await setDoc(doc(db, "accounts_login", sessionToken), {
      guid: account_guid,
      sessiontoken: sessionToken,
      email: emailid,
      name: displayName,
      photoURL,
      login_time: new Date(),
      status: true,
    });

    const cookieOptions = getCookieOptions();

    Cookies.set("status_user", "login", cookieOptions);
    Cookies.set("emailid", emailid, cookieOptions);
    Cookies.set("username", displayName, cookieOptions);
    Cookies.set("guid", account_guid, cookieOptions);
    Cookies.set("photoURL", photoURL, cookieOptions);
    Cookies.set("sessiontoken", sessionToken, cookieOptions);

    return { success: true };

  } catch (error) {
    console.error("Google login error:", error);
    return { success: false };
  }
};

/* ============================
   🚪 LOGOUT
============================ */
export const googleLogout = () => {
  Cookies.remove("status_user", { path: "/" });
  Cookies.remove("sessiontoken", { path: "/" });
  Cookies.remove("guid", { path: "/" });
  Cookies.remove("emailid", { path: "/" });
  Cookies.remove("username", { path: "/" });

  window.location.href = "/";
};

export { auth, db };